import {
  DriverLocationResponse,
  Order,
  OrderDeliveryType,
  Route,
} from "@web-app/types";

interface FocusOrderOptions {
  driverLocations?: DriverLocationResponse;
  mapRef: google.maps.Map;
  order: Order;
}

interface FocusRouteOptions {
  driverLocations?: DriverLocationResponse;
  mapRef: google.maps.Map;
  orders: Map<string, Order>;
  route: Route;
}

export const focusOnOrder = ({
  driverLocations = {},
  mapRef,
  order,
}: FocusOrderOptions) => {
  if (!mapRef || !order) return;

  const isRoutedOrder =
    order.routeId &&
    (order.type === OrderDeliveryType.ROUTE_OPT_PICKUP ||
      order.type === OrderDeliveryType.ROUTE_OPT_DROPOFF);

  if (isRoutedOrder) {
    let targetLocation: google.maps.LatLng | null = null;

    if (order.type === OrderDeliveryType.ROUTE_OPT_PICKUP) {
      targetLocation = new google.maps.LatLng(
        order.pickupLocation.coordinates[1],
        order.pickupLocation.coordinates[0],
      );
    } else if (order.type === OrderDeliveryType.ROUTE_OPT_DROPOFF) {
      targetLocation = new google.maps.LatLng(
        order.dropoffLocation.coordinates[1],
        order.dropoffLocation.coordinates[0],
      );
    }

    if (targetLocation) {
      google.maps.event.trigger(mapRef, "resize");

      requestAnimationFrame(() => {
        const currentCenter = mapRef.getCenter();

        if (currentCenter && google.maps.geometry?.spherical) {
          const distance =
            google.maps.geometry.spherical.computeDistanceBetween(
              currentCenter,
              targetLocation,
            );

          if (distance > 50000) {
            mapRef.setCenter(targetLocation);
            mapRef.setZoom(14);
          } else {
            mapRef.panTo(targetLocation);
            mapRef.setZoom(14);
          }
        } else {
          mapRef.setCenter(targetLocation);
          mapRef.setZoom(14);
        }
      });
    }
    return;
  }

  const bounds = new window.google.maps.LatLngBounds();
  let hasBounds = false;

  if (
    order.type === OrderDeliveryType.ROUTE_OPT_PICKUP ||
    order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF
  ) {
    bounds.extend(
      new google.maps.LatLng(
        order.pickupLocation.coordinates[1],
        order.pickupLocation.coordinates[0],
      ),
    );
    hasBounds = true;
  }

  if (
    order.type === OrderDeliveryType.ROUTE_OPT_DROPOFF ||
    order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF
  ) {
    bounds.extend(
      new google.maps.LatLng(
        order.dropoffLocation.coordinates[1],
        order.dropoffLocation.coordinates[0],
      ),
    );
    hasBounds = true;
  }

  if (order.assigneeId && driverLocations[order.assigneeId]) {
    const driverLocation = driverLocations[order.assigneeId];
    bounds.extend(
      new google.maps.LatLng(driverLocation.latitude, driverLocation.longitude),
    );
    hasBounds = true;
  }

  if (!hasBounds) return;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const isSinglePoint = ne.equals(sw);

  if (isSinglePoint) {
    const currentCenter = mapRef.getCenter();

    if (currentCenter && google.maps.geometry?.spherical) {
      const distance = google.maps.geometry.spherical.computeDistanceBetween(
        currentCenter,
        ne,
      );

      if (distance > 50000) {
        mapRef.setCenter(ne);
        mapRef.setZoom(11);
      } else {
        mapRef.panTo(ne);
        const currentZoom = mapRef.getZoom();
        if (currentZoom && (currentZoom < 11 || currentZoom > 14)) {
          mapRef.setZoom(11);
        }
      }
    } else {
      mapRef.setCenter(ne);
      mapRef.setZoom(11);
    }
  } else {
    google.maps.event.trigger(mapRef, "resize");

    requestAnimationFrame(() => {
      mapRef.fitBounds(bounds);

      setTimeout(() => {
        const currentZoom = mapRef.getZoom();
        if (currentZoom) {
          const targetZoom = Math.max(10, currentZoom - 1);
          mapRef.setZoom(targetZoom);
        }
      }, 50);
    });
  }
};

export const focusOnRoute = ({
  driverLocations = {},
  mapRef,
  orders,
  route,
}: FocusRouteOptions) => {
  if (!mapRef || !route) return;

  const bounds = new window.google.maps.LatLngBounds();
  let hasBounds = false;

  route.ordersOrder.forEach((orderItem) => {
    const order = orders.get(orderItem.id);
    if (!order) return;

    if (
      order.type === OrderDeliveryType.ROUTE_OPT_PICKUP ||
      order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF
    ) {
      bounds.extend(
        new google.maps.LatLng(
          order.pickupLocation.coordinates[1],
          order.pickupLocation.coordinates[0],
        ),
      );
      hasBounds = true;
    }

    if (
      order.type === OrderDeliveryType.ROUTE_OPT_DROPOFF ||
      order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF
    ) {
      bounds.extend(
        new google.maps.LatLng(
          order.dropoffLocation.coordinates[1],
          order.dropoffLocation.coordinates[0],
        ),
      );
      hasBounds = true;
    }
  });

  if (route.assigneeId && driverLocations[route.assigneeId]) {
    const driverLocation = driverLocations[route.assigneeId];
    bounds.extend(
      new google.maps.LatLng(driverLocation.latitude, driverLocation.longitude),
    );
    hasBounds = true;
  }

  if (!hasBounds) return;

  mapRef.fitBounds(bounds);

  setTimeout(() => {
    const currentZoom = mapRef.getZoom();
    if (currentZoom) {
      const targetZoom = Math.max(9, currentZoom - 1);
      mapRef.setZoom(targetZoom);
    }
  }, 50);
};
